import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useTeamPerformance } from "@/hooks/useTeamPerformance";
import { Users, TrendingUp, Award } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

export default function TeamPerformanceComparison() {
  const { data: teams, isLoading } = useTeamPerformance();

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">جاري التحميل...</div>
      </div>
    );
  }

  if (!teams || teams.length === 0) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <h1 className="text-3xl font-bold">مقارنة أداء الفرق</h1>
        <Card>
          <CardContent className="p-12 text-center space-y-4">
            <Users className="h-16 w-16 mx-auto text-muted-foreground" />
            <h2 className="text-2xl font-bold">لا توجد بيانات أداء للفرق</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              لم يتم إضافة أي بيانات أداء للفرق بعد. سيتم تحديث هذه البيانات تلقائياً بناءً على أداء الموظفين في كل قسم.
            </p>
            <div className="pt-4">
              <p className="text-sm text-muted-foreground">
                💡 ستظهر البيانات هنا بعد:
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• إضافة مقاييس أداء للموظفين</li>
                <li>• تسجيل الحجوزات والمبيعات</li>
                <li>• إنجاز المهام</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const topTeam = teams[0];

  return (
    <div className="container mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold">مقارنة أداء الفرق</h1>

      {topTeam && (
        <Card className="border-2 border-yellow-400">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-6 w-6 text-yellow-600" />
              الفريق الأفضل: {topTeam.team_name}
            </CardTitle>
            <CardDescription>{topTeam.department}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-2xl font-bold">{topTeam.team_score}%</div>
                <div className="text-sm text-muted-foreground">النتيجة</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{topTeam.total_revenue.toLocaleString()} ر.س</div>
                <div className="text-sm text-muted-foreground">الإيرادات</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {teams?.map((team, idx) => (
          <Card key={team.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Badge>#{idx + 1}</Badge>
                    {team.team_name}
                  </CardTitle>
                  <CardDescription>{team.department}</CardDescription>
                </div>
                <div className="text-2xl font-bold">{team.team_score}%</div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <Users className="h-4 w-4 mb-1" />
                  <div className="font-bold">{team.team_size} أعضاء</div>
                </div>
                <div>
                  <TrendingUp className="h-4 w-4 mb-1" />
                  <div className="font-bold">{team.total_bookings} حجز</div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>معدل التحويل</span>
                  <span>{team.avg_conversion_rate}%</span>
                </div>
                <Progress value={team.avg_conversion_rate} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}